import React from 'react';
import { BiErrorCircle } from 'react-icons/bi';
import { IoMdArrowDropdown } from 'react-icons/io';

import { Popover, Tooltip } from 'antd';
import EditableCell from 'components/common/EditableCell';
import NumericInputField from 'components/common/NumericInputField';
import {
  CHANGE_SEED_TARGET_MENU_LABEL,
  SEED_TARGET_MISSING_TOOLTIP,
  TASKS_GRID_REORDER_STEP_ARIA_LABEL_PREFIX,
  TASKS_GRID_SELECT_UNIT_PLACEHOLDER,
  TASKS_GRID_STEP_ACTIONS_ARIA_LABEL,
} from 'constants/uiConstants';
import { useSelectedInput } from 'hooks/inputs/useSelectedInput';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import {
  IInputStep,
  IInputStepSeed,
  IInputStepSeedDress,
  InputStepType,
  SeedDressRateMode,
} from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import { formatCurrency } from 'utils/CurrencyUtils';
import MergeClassNames from 'utils/MergeClassNames';

import {
  focusAdjacentStepCell,
  STEP_NAV_COL_ATTR,
  STEP_NAV_COL_COST,
  STEP_NAV_COL_RATE,
} from './focusAdjacentStepCell';
import SeedTargetList from './SeedTargetList';
import {
  buildSeedLinkedReason,
  calculateStepCostPerArea,
  calculateStepRequired,
  formatStepCalculation,
  getLinkedSeedDressItems,
  getRateUnitOptions,
  getStepCost,
  getStepDisplayLabel,
  getStepRate,
  getStepUnitLabel,
  NO_CALCULATION_STEP_TYPES,
} from './stepCalculations';
import { getStepSelectorGroupsForTask } from './stepConfig';
import { StepSelection } from './stepFactory';
import StepInputSelector from './StepInputSelector';
import { RowGripButton, RowKebabButton } from './TaskRowContextMenu';
import type { useStepContextMenu } from './useStepContextMenu';
import type { useStepReorder } from './useStepReorder';

type StepReorderApi = ReturnType<typeof useStepReorder>;
type StepContextMenuApi = ReturnType<typeof useStepContextMenu>;

interface TaskStepRowProps {
  task: IInputTask;
  step: IInputStep;
  isLastRow: boolean;
  /** The product's analysis in a focused filter view (nutrients or MoA). */
  detailLine?: string | null;
  hasAmbiguousStepIds: boolean;
  canReorder: boolean;
  gUnits: GUnit[];
  editingStepInputId: string | null;
  setEditingStepInputId: (stepId: string | null) => void;
  handleStepInputChange: (
    task: IInputTask,
    stepId: string,
    selections: StepSelection[]
  ) => void;
  commitStepField: (
    task: IInputTask,
    stepId: string,
    patch: Partial<IInputStep>
  ) => void;
  stepReorder: StepReorderApi;
  stepContextMenu: StepContextMenuApi;
}

// One row of the step sub-table: the "change input" popover, Rate/Rate
// unit/Seed-to-be-treated/Cost/Required/$-ha cells, the re-order grip and
// drag-and-drop wiring, and the row's own kebab actions button.
const TaskStepRow: React.FC<TaskStepRowProps> = ({
  task,
  step,
  isLastRow,
  detailLine,
  hasAmbiguousStepIds,
  canReorder,
  gUnits,
  editingStepInputId,
  setEditingStepInputId,
  handleStepInputChange,
  commitStepField,
  stepReorder,
  stepContextMenu,
}) => {
  const { reorderTarget, armedDragStepId, dragOverStepId } = stepReorder;
  const { contextMenuState, seedTargetEditTarget } = stepContextMenu;
  const selectedInput = useSelectedInput();

  const isEditingSeedTarget =
    seedTargetEditTarget?.taskId === task.taskId &&
    seedTargetEditTarget?.stepId === step.stepId;

  const isReordering =
    reorderTarget?.taskId === task.taskId &&
    reorderTarget.stepId === step.stepId;
  // Suppressed when stepIds repeat: comparing by id alone would light up
  // every row that shares it, misleading the user about which row they
  // targeted.
  const isHighlighted =
    !hasAmbiguousStepIds &&
    contextMenuState?.target.rowType === 'step' &&
    contextMenuState.target.taskId === task.taskId &&
    contextMenuState.target.stepId === step.stepId;
  // Only ever set for a row in the dragged step's own task, so the id alone
  // identifies the target.
  const isDropTarget = dragOverStepId === step.stepId;
  const rate = getStepRate(step);
  const cost = getStepCost(step);
  const required = selectedInput
    ? calculateStepRequired(step, task, selectedInput, gUnits)
    : null;
  const costPerArea = calculateStepCostPerArea(step, task, gUnits);
  const stepDisplayLabel = getStepDisplayLabel(
    step as IInputStepSeed | IInputStepSeedDress,
    task
  );
  const isMissingSeedTarget =
    step.stepType === InputStepType.SeedDress &&
    step.rateMode === SeedDressRateMode.Per100Kg &&
    !step.linkedStepId;
  // When this is a Seed step linked by at least one SeedDress, switching step
  // type in the picker would leave those treatments with a dangling reference.
  // Compute a human-readable reason naming the specific blocking treatments so
  // the locked category-tab tooltip tells the user exactly what to delete first.
  const seedLinkedTypeChangeReason = (() => {
    if (step.stepType !== InputStepType.Seed) return undefined;
    const items = getLinkedSeedDressItems(task, step.stepId);
    return items.length > 0
      ? buildSeedLinkedReason(items, ' to change step type')
      : undefined;
  })();
  const cellBorder = MergeClassNames(
    'border-gray-medium',
    !isLastRow && 'border-b'
  );
  const inputNameCellBorder = MergeClassNames(
    isMissingSeedTarget
      ? 'bg-information border-gray-medium'
      : 'border-gray-medium',
    !isLastRow && 'border-b'
  );
  const isOperationRow = NO_CALCULATION_STEP_TYPES.has(step.stepType);
  const hasCost = 'cost' in step;
  const currentAppUnit = 'appUnit' in step ? (step.appUnit ?? null) : null;
  const stepInputCategoryKey = getStepSelectorGroupsForTask(task.taskType).find(
    (group) => group.stepType === step.stepType
  )?.key;

  const renderStepDisplayLabel = stepDisplayLabel && (
    <div className="flex flex-row items-center gap-1">
      <div>{stepDisplayLabel.primaryLabel}</div>
      {stepDisplayLabel.secondaryLabel && (
        <div className="text-xs font-semibold text-gray-muted">
          {`(${stepDisplayLabel.secondaryLabel})`}
        </div>
      )}
    </div>
  );

  return (
    <tr
      className={MergeClassNames(
        // `group` drives the three-dot reveal. No row-level hover here —
        // outline/border on a <tr> doesn't reliably form a continuous
        // rectangle across real browsers (tried and dropped). Hover feedback
        // is field-level only (EditableCell's own border-on-hover).
        'group bg-white',
        isHighlighted && 'highlighted-row',
        isReordering && 'step-row-reordering',
        isDropTarget && 'step-row-drop-target'
      )}
      // Draggable only while the pointer rests on this row's handle, so
      // cells stay selectable. This is not the re-order gate, though — see
      // handleStepDragStart, which rejects anything whose press did not
      // begin on the grip.
      draggable={canReorder && armedDragStepId === step.stepId}
      onDragStart={(event) =>
        stepReorder.handleStepDragStart(event, task.taskId, step.stepId)
      }
      // dragenter as well as dragover: cancelling only the latter shows one
      // frame of the "no drop" cursor on entering a valid row.
      onDragEnter={(event) =>
        stepReorder.handleStepDragOver(event, task.taskId, step.stepId)
      }
      onDragOver={(event) =>
        stepReorder.handleStepDragOver(event, task.taskId, step.stepId)
      }
      onDragLeave={(event) =>
        stepReorder.handleStepDragLeave(event, step.stepId)
      }
      onDrop={(event) => stepReorder.handleStepDrop(event, task, step.stepId)}
      // Fires however the drag ends — dropped, cancelled with Escape, or
      // released outside the grid — so it is the one place that must leave
      // no state behind.
      onDragEnd={stepReorder.clearStepDrag}
      onClick={(event) => {
        event.stopPropagation();
        stepContextMenu.closeContextMenu();
      }}
      onContextMenu={(event) =>
        stepContextMenu.handleStepRowContextMenu(
          event,
          task.taskId,
          step.stepId
        )
      }
    >
      <td
        className={MergeClassNames(
          'relative border-l border-r p-0',
          inputNameCellBorder
        )}
      >
        {/* Sibling of the Popover, not a child of its trigger — inside it,
            clicking the grip would open the input picker. Positioned in the
            wrapper's pl-6 lane, left of the grid's own border, so the Input
            name column stays purely the product name and nothing shifts
            when a filter makes the grip come or go.

            Biased toward the row rather than centred in that lane
            (justify-end + pr-0.5): proximity is what tells a user which row
            the control belongs to, so the gap to the grid is deliberately
            smaller than the gap to the green rail. */}
        {canReorder && (
          <div className="absolute inset-y-0 -left-6 z-10 flex w-6 items-center justify-end pr-0.5">
            <RowGripButton
              label={`${TASKS_GRID_REORDER_STEP_ARIA_LABEL_PREFIX} ${step.item}`}
              isActive={isReordering}
              onActivate={(event) => {
                event.preventDefault();
                event.stopPropagation();
                stepReorder.startReorder(task.taskId, step.stepId);
              }}
              onArmDrag={(armed) =>
                stepReorder.setStepDragArmed(step.stepId, armed)
              }
              onPressStart={() => stepReorder.setDragOrigin(step.stepId)}
            />
          </div>
        )}
        <Popover
          trigger="click"
          placement="right"
          autoAdjustOverflow
          destroyOnHidden
          getPopupContainer={() => document.body}
          open={editingStepInputId === step.stepId || isEditingSeedTarget}
          onOpenChange={(open) => {
            stepContextMenu.closeContextMenu();
            if (!open) {
              setEditingStepInputId(null);
              stepContextMenu.closeSeedTargetEdit();
              return;
            }
            setEditingStepInputId(!hasAmbiguousStepIds ? step.stepId : null);
          }}
          classNames={{
            root: 'step-input-selector-popover',
          }}
          content={
            isEditingSeedTarget && step.stepType === InputStepType.SeedDress ? (
              <div className="w-72 rounded-md bg-white p-3">
                <div className="mb-2 text-base font-semibold text-charcoal">
                  {CHANGE_SEED_TARGET_MENU_LABEL}
                </div>
                <SeedTargetList
                  task={task}
                  highlightedStepId={step.linkedStepId}
                  onSelect={(linkedStepId) => {
                    commitStepField(task, step.stepId, { linkedStepId });
                    stepContextMenu.closeSeedTargetEdit();
                  }}
                />
              </div>
            ) : (
              <StepInputSelector
                taskType={task.taskType}
                initialCategoryKey={stepInputCategoryKey}
                initialSearchText={step.item}
                initialCropGlobalId={
                  step.stepType === InputStepType.Seed
                    ? step.globalId
                    : undefined
                }
                initialCropName={
                  step.stepType === InputStepType.Seed ? step.item : undefined
                }
                initialVarietyGlobalId={
                  step.stepType === InputStepType.Seed
                    ? step.globalId2
                    : undefined
                }
                initialVarietyName={
                  step.stepType === InputStepType.Seed ? step.item2 : undefined
                }
                initialSeedDressStep={
                  step.stepType === InputStepType.SeedDress ? step : undefined
                }
                onClose={() => setEditingStepInputId(null)}
                onSelect={(selection) =>
                  handleStepInputChange(task, step.stepId, selection)
                }
                task={task}
                lockedCategoryReason={seedLinkedTypeChangeReason}
              />
            )
          }
        >
          <div className="flex h-full w-full cursor-pointer items-center border border-transparent px-2 py-1 hover:z-10 hover:border-success">
            <div className="block w-full min-w-0 flex-1">
              <div className="truncate">{renderStepDisplayLabel}</div>
              {detailLine && (
                <div className="truncate text-xs font-normal text-gray-dark">
                  {detailLine}
                </div>
              )}
            </div>
            {isMissingSeedTarget && (
              <Tooltip title={SEED_TARGET_MISSING_TOOLTIP}>
                <span className="ml-1 flex flex-shrink-0 items-center">
                  <BiErrorCircle
                    className="text-lg text-danger-dark"
                    aria-hidden="true"
                  />
                </span>
              </Tooltip>
            )}
            <IoMdArrowDropdown className="ml-1 flex-shrink-0 text-gray-dark" />
          </div>
        </Popover>
      </td>
      <td
        className={MergeClassNames('relative border-r p-0', cellBorder)}
        {...{ [STEP_NAV_COL_ATTR]: STEP_NAV_COL_RATE }}
      >
        <div className="absolute inset-0">
          {isOperationRow ? (
            <div className="flex h-full w-full items-center border border-transparent bg-secondary-light px-2 py-1 font-medium">
              {rate === null ? ' ' : rate}
            </div>
          ) : (
            <NumericInputField
              className="h-full w-full"
              inputClassName="h-full w-full"
              min={0}
              inclusiveMin
              allowEmpty
              disabled={hasAmbiguousStepIds}
              value={rate}
              displayValue={rate === null ? ' ' : rate}
              onArrowNavigate={focusAdjacentStepCell}
              onCommit={(value) =>
                commitStepField(task, step.stepId, { rate: value })
              }
            />
          )}
        </div>
      </td>
      <td className={MergeClassNames('relative border-r p-0', cellBorder)}>
        <div className="absolute inset-0">
          {isOperationRow ? (
            <div className="flex h-full w-full items-center border border-transparent bg-secondary-light px-2 py-1 font-medium">
              {' '}
            </div>
          ) : (
            <EditableCell
              type="select"
              className="h-full w-full"
              disabled={hasAmbiguousStepIds}
              value={currentAppUnit?.globalId ?? null}
              displayValue={getStepUnitLabel(step)}
              options={getRateUnitOptions(step.stepType, gUnits)}
              placeholder={TASKS_GRID_SELECT_UNIT_PLACEHOLDER}
              onCommit={(globalId) => {
                const selected = gUnits.find(
                  (unit) => unit.globalId === globalId
                );
                if (!selected) return;
                commitStepField(task, step.stepId, {
                  appUnit: { unit: selected.code, globalId: selected.globalId },
                });
              }}
            />
          )}
        </div>
      </td>
      <td
        className={MergeClassNames('relative border-r p-0', cellBorder)}
        {...{ [STEP_NAV_COL_ATTR]: STEP_NAV_COL_COST }}
      >
        <div className="absolute inset-0">
          {hasCost ? (
            <NumericInputField
              className="h-full w-full"
              inputClassName="h-full w-full"
              min={0}
              inclusiveMin
              allowEmpty
              disabled={hasAmbiguousStepIds}
              value={cost}
              displayValue={cost === null ? ' ' : formatCurrency(cost)}
              onArrowNavigate={focusAdjacentStepCell}
              onCommit={(value) =>
                commitStepField(task, step.stepId, { cost: value })
              }
            />
          ) : (
            <div className="flex h-full w-full items-center border border-transparent bg-secondary-light px-2 py-1 font-medium">
              {' '}
            </div>
          )}
        </div>
      </td>
      <td className={MergeClassNames('relative border-r p-0', cellBorder)}>
        <div className="absolute inset-0 flex items-center border border-transparent bg-secondary-light px-2 py-1 text-sm font-semibold">
          {formatStepCalculation(required)}
        </div>
      </td>
      <td className={MergeClassNames('relative border-r p-0', cellBorder)}>
        <div className="absolute inset-0 flex items-center justify-end border border-transparent bg-secondary-light px-2 py-1 text-sm font-semibold">
          {costPerArea === null ? ' ' : formatCurrency(costPerArea)}
        </div>
        {/* Sibling of the value div, not a child of it: positioned in the
            wrapper's own pr-7 lane, right of the grid's border — mirrors the
            grip handle's lane on the opposite side, so the fill isn't
            squeezed to make room for the button anymore (design reference:
            the dots sit outside the table, not inside its last column). */}
        <div className="absolute inset-y-0 -right-6 z-10 flex w-6 items-center justify-center">
          <RowKebabButton
            label={TASKS_GRID_STEP_ACTIONS_ARIA_LABEL}
            isActive={isHighlighted}
            onOpen={(event) =>
              stepContextMenu.openMenuFromKebab(event, {
                rowType: 'step',
                taskId: task.taskId,
                stepId: step.stepId,
              })
            }
          />
        </div>
      </td>
    </tr>
  );
};

export default TaskStepRow;
